import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiLayers, FiList, FiFolderPlus, FiAlertTriangle, FiFileText } = FiIcons;

const CategoryManagement = () => {
  const navigate = useNavigate();
  const { reportCategories, ruhCategories, updateCategories, updateRuhCategories } = useData();
  
  // State for tabs
  const [activeTab, setActiveTab] = useState('hms'); // 'hms' or 'ruh'

  // Local state for editing
  const [localHmsCategories, setLocalHmsCategories] = useState(reportCategories);
  const [localRuhCategories, setLocalRuhCategories] = useState(ruhCategories);
  
  // Update local state when context changes (initial load)
  useEffect(() => {
    setLocalHmsCategories(reportCategories);
    setLocalRuhCategories(ruhCategories);
  }, [reportCategories, ruhCategories]);

  // Determine which state to use based on active tab
  const activeCategories = activeTab === 'hms' ? localHmsCategories : localRuhCategories;
  const setActiveCategories = activeTab === 'hms' ? setLocalHmsCategories : setLocalRuhCategories;

  // State for new main category modal
  const [showNewCatModal, setShowNewCatModal] = useState(false);
  const [newCatForm, setNewCatForm] = useState({ label: '', color: 'blue' });

  const handleSave = () => {
    if (activeTab === 'hms') {
      updateCategories(localHmsCategories);
    } else {
      updateRuhCategories(localRuhCategories);
    }
    alert(`${activeTab === 'hms' ? 'HMS' : 'RUH'} kategorier oppdatert!`);
  };

  // --- SUBCATEGORY LOGIC ---
  const handleAddSubCategory = (mainCatKey) => {
    const newSub = { id: `new_${Date.now()}`, name: 'Ny Underkategori' };
    setActiveCategories(prev => ({
        ...prev,
        [mainCatKey]: {
            ...prev[mainCatKey],
            categories: [...prev[mainCatKey].categories, newSub]
        }
    }));
  };

  const updateSubCatName = (mainCatKey, subCatId, newName) => {
    setActiveCategories(prev => ({
        ...prev,
        [mainCatKey]: {
            ...prev[mainCatKey],
            categories: prev[mainCatKey].categories.map(c => c.id === subCatId ? { ...c, name: newName } : c)
        }
    }));
  };

  const deleteSubCategory = (mainCatKey, subCatId) => {
    if(!window.confirm("Slette denne underkategorien?")) return;
    setActiveCategories(prev => ({
        ...prev,
        [mainCatKey]: {
            ...prev[mainCatKey],
            categories: prev[mainCatKey].categories.filter(c => c.id !== subCatId)
        }
    }));
  };

  // --- MAIN CATEGORY LOGIC ---
  const handleCreateMainCategory = (e) => {
    e.preventDefault();
    if (!newCatForm.label) return;

    const prefix = activeTab === 'hms' ? 'cat' : 'ruh';
    const newKey = `${prefix}_${Date.now()}`;
    
    setActiveCategories(prev => ({
      ...prev,
      [newKey]: {
        label: newCatForm.label,
        color: newCatForm.color,
        categories: [] // Start empty
      }
    }));
    
    setShowNewCatModal(false);
    setNewCatForm({ label: '', color: 'blue' });
  };

  const deleteMainCategory = (key) => {
    if(!window.confirm("ER DU SIKKER? Dette vil slette hovedkategorien og ALLE underkategorier under den.")) return;
    const newCats = { ...activeCategories };
    delete newCats[key];
    setActiveCategories(newCats);
  };

  const colors = ['red', 'orange', 'blue', 'green', 'purple', 'slate', 'indigo', 'teal', 'pink', 'cyan'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-8 py-4 sticky top-0 z-10">
        <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
                <h1 className="text-xl font-black text-gray-900">Kategoristyring (Master)</h1>
            </div>
            <div className="flex gap-3">
                <button onClick={() => setShowNewCatModal(true)} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-gray-50">
                    <SafeIcon icon={FiFolderPlus} /> Ny Hovedkategori
                </button>
                <button onClick={handleSave} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-indigo-700">
                    <SafeIcon icon={FiSave} /> Lagre Endringer
                </button>
            </div>
        </div>

        {/* TABS */}
        <div className="flex gap-6 border-b border-gray-100">
            <button 
                onClick={() => setActiveTab('hms')} 
                className={`pb-3 px-2 flex items-center gap-2 font-bold text-sm transition-all border-b-2 ${activeTab === 'hms' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
                <SafeIcon icon={FiAlertTriangle} /> HMS Kategorier
            </button>
            <button 
                onClick={() => setActiveTab('ruh')} 
                className={`pb-3 px-2 flex items-center gap-2 font-bold text-sm transition-all border-b-2 ${activeTab === 'ruh' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
                <SafeIcon icon={FiFileText} /> RUH Kategorier
            </button>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Object.entries(activeCategories || {}).map(([key, section]) => (
                <div key={key} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full group/card relative">
                    {/* Main Cat Header */}
                    <div className={`flex items-center justify-between mb-4 pb-2 border-b border-${section.color}-100`}>
                        <h3 className={`font-black text-${section.color}-600 text-sm uppercase tracking-wide`}>{section.label}</h3>
                        <div className="flex items-center gap-2">
                            <SafeIcon icon={FiLayers} className={`text-${section.color}-400`} />
                            <button onClick={() => deleteMainCategory(key)} className="text-gray-300 hover:text-red-600 opacity-0 group-hover/card:opacity-100 transition-opacity p-1">
                                <SafeIcon icon={FiTrash2} size={14} />
                            </button>
                        </div>
                    </div>
                    
                    {/* Subcats List */}
                    <div className="space-y-3 flex-1">
                        {section.categories.map(cat => (
                            <div key={cat.id} className="flex items-center gap-2 group">
                                <SafeIcon icon={FiList} className="text-gray-300 text-xs mt-1" />
                                <input 
                                    type="text" 
                                    value={cat.name} 
                                    onChange={(e) => updateSubCatName(key, cat.id, e.target.value)}
                                    className="flex-1 text-sm text-gray-700 border-none bg-transparent focus:bg-gray-50 focus:ring-2 focus:ring-indigo-100 rounded px-2 py-1"
                                />
                                <button onClick={() => deleteSubCategory(key, cat.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity p-1">
                                    <SafeIcon icon={FiX} size={14} />
                                </button>
                            </div>
                        ))}
                        {section.categories.length === 0 && (
                            <p className="text-xs text-gray-400 italic">Ingen underkategorier</p>
                        )}
                    </div>

                    {/* Add Subcat Button */}
                    <button onClick={() => handleAddSubCategory(key)} className="mt-6 w-full py-2 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-xs font-bold hover:border-indigo-300 hover:text-indigo-600 transition-all flex items-center justify-center gap-2">
                        <SafeIcon icon={FiPlus} /> Legg til underkategori
                    </button>
                </div>
            ))}
        </div>
      </div>

      {/* MODAL: NEW MAIN CATEGORY */}
      {showNewCatModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-bold mb-4">Ny Hovedkategori ({activeTab.toUpperCase()})</h3>
                <form onSubmit={handleCreateMainCategory}>
                    <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Navn / Tittel</label>
                        <input 
                            type="text" 
                            autoFocus
                            required
                            className="w-full border border-gray-300 rounded-lg p-2"
                            placeholder="F.eks. IT og Sikkerhet"
                            value={newCatForm.label}
                            onChange={e => setNewCatForm({...newCatForm, label: e.target.value})}
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Fargekode</label>
                        <div className="flex gap-2 flex-wrap">
                            {colors.map(c => (
                                <button
                                    type="button"
                                    key={c}
                                    onClick={() => setNewCatForm({...newCatForm, color: c})}
                                    className={`w-8 h-8 rounded-full bg-${c}-500 ${newCatForm.color === c ? 'ring-4 ring-offset-2 ring-gray-300' : ''}`}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setShowNewCatModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Avbryt</button>
                        <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold">Opprett</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;